import dateFormat from 'dateformat';
import { Flex, Text } from '@chakra-ui/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faTriangleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import {
  endStates,
  IEndState,
  IExpertPhase,
  IFirstAssessmentRescission,
  IWorkPhase
} from '@publishing-engine/domain/models';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { getAssessmentPhaseContent, getPhaseContent } from 'src/utils/phaseCopy';
import { Button } from '@morressier/alembic';
import { adjudicationStatusBadges } from 'src/components/Review/Badges';
import ContextMenu from 'src/components/Submission/ContextMenu';
import { useState } from 'react';
import NoteFromEditorModal from '../Assessment/NoteFromEditorModal';
import { useAdjudicationData } from 'src/contexts/AdjudicationDataProvider';
import AdjudicationFeedbackForm from 'src/pages/MyTask/Assessments/[id]/AdjudicationFeedbackForm';
import ReviewFeedbackModal from './ReviewFeedbackModal';
import { EndState } from '../EndState';
import { getAssessmentCompletionDate } from '../Assessment/AssessmentPhase';
import { formatDeadlineDate } from '../../utils/date';

export interface DeadlineExtensionDetails {
  currentState: string;
  currentDeadline: string;
  nextDeadline: string;
  deadlineExtensionInDays: number;
}

interface Props {
  deadlineRequested?: boolean;
  deadlineExtensionDetails?: DeadlineExtensionDetails | null;
  firstAssessmentRescission?: IFirstAssessmentRescission;
  remainingDeadlineAutoExtension?: number;
  submissionCurrentWorkflowState?: string;
}

export const InAdjudicationSidePanel: React.FC<Props> = () => {
  const {
    adjudication,
    workflowResponse,
    hasSubmittedAdjudicationInCurrentRound,
    isInviteRequestAccepted: isAssessmentRequestAccepted
  } = useAdjudicationData();
  const reviewDeadline = adjudication.workflow.currentDeadline
    ? new Date(adjudication.workflow.currentDeadline)
    : null;
  const reviewCurrentState = adjudication.workflow.currentState;
  const { heading, description: defaultDescription } = getPhaseContent(
    workflowResponse?.workflowData.currentState as IWorkPhase,
    'expert'
  );

  const submissionPhaseHeading = heading ? heading : getAssessmentPhaseContent(adjudication.workflow.currentState as IExpertPhase).heading
  const isEndState = endStates.includes(
    workflowResponse?.workflowData.currentState as IEndState
  );
  const description = getAssessmentPhaseContent(
    adjudication.workflow.currentState as IExpertPhase,
    {
      dateSent: dateFormat(
        getAssessmentCompletionDate(workflowResponse?.workflowData),
        'mmm d, yyyy'
      )
    }
  ).description ?? defaultDescription;
  const [isPrepareFeedbackModal, setIsPrepareFeedbackModal] =
    useState<boolean>(false);
  const [isSubmitFeedbackModal, setIsSubmitFeedbackModal] =
    useState<boolean>(false);
  const [isNoteFromEditorModal, setIsNoteFromEditorModal] = useState(false);
  const [isReviewFeedbackModal, setIsReviewFeedbackModal] = useState(false);
  const adjudicationCurrentState = adjudication?.workflow?.currentState

  const contextMenuActions: { name: string; action: () => void }[] = [
    {
      name: 'View Note from the Editor',
      action: () => setIsNoteFromEditorModal(true)
    },
    {
      name: 'Prepare Feedback',
      action: () => {
        setIsPrepareFeedbackModal(prev => !prev)
      }
    }
  ];

  return (
    <>
      {isPrepareFeedbackModal && (
        <AdjudicationFeedbackForm
          onClose={() => setIsPrepareFeedbackModal(prev => !prev)}
          pageText="Prepare Feedback"
          subHeading="Draft and adjust a response to the Editors while assessing this Submission."
        />
      )}
      {isSubmitFeedbackModal && (
        <AdjudicationFeedbackForm
          onClose={() => setIsSubmitFeedbackModal(prev => !prev)}
          pageText="Submit Feedback"
          subHeading="By proceeding, you are confirming that your Review is complete and accurate. Your defined communications will be sent out to Editors and Authors, and cannot be modified after Submission."
        />
      )}
      <NoteFromEditorModal
        onClose={() => setIsNoteFromEditorModal(false)}
        isOpen={isNoteFromEditorModal}
        editorialRequest={adjudication.caseData?.editorialRequest}
      />
      <ReviewFeedbackModal
        onClose={() => setIsReviewFeedbackModal(false)}
        isOpen={isReviewFeedbackModal}
      />

      {isEndState ? (
        <>
          <EndState
            role="expert"
            currentWorkflowState={
              workflowResponse?.workflowData.currentState as IEndState
            }
            dateEnteredCurrentPhase={
              workflowResponse.workflowData.dateEnteredCurrentPhase
            }
            assessmentStatus={adjudicationStatusBadges[reviewCurrentState === "reviewed" ? "completed" : reviewCurrentState || ""]}
          />

          {adjudicationCurrentState === 'reviewed' && <Flex justifyContent="flex-end" gap={4} padding={4}>
            <Button
              variant="primary"
              onClick={() => setIsReviewFeedbackModal(prev => !prev)}
            >
              Review Feedback
            </Button>
          </Flex>}
        </>
      ) :
        <Flex flexDirection="column">
          <Flex flexDirection="column" gap={2}>
            <Flex gap={2} backgroundColor="surfaceSecondaryFill" padding={4}>
              <Text textStyle="headingItem">{submissionPhaseHeading}</Text>
              {adjudicationStatusBadges[reviewCurrentState === "reviewed" ? "completed" : reviewCurrentState || ""]}

              {!hasSubmittedAdjudicationInCurrentRound() && reviewDeadline && (
                <Flex
                  marginLeft="auto"
                  gap={2}
                  textColor={
                    calculateDaysToDeadline(reviewDeadline) < 1
                      ? 'errorFillInverse'
                      : 'black'
                  }
                >
                  Due {formatDeadlineDate(reviewDeadline)}
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    width={6}
                    height={6}
                    backgroundColor={
                      calculateDaysToDeadline(reviewDeadline) < 1
                        ? 'errorFillInverse'
                        : 'attentionSecondaryFill'
                    }
                    marginRight={2}
                    color={
                      calculateDaysToDeadline(reviewDeadline) < 1
                        ? 'neutralPrimaryContentInverse'
                        : 'successPrimaryFill'
                    }
                    borderRadius="small"
                  >
                    <FontAwesomeIcon
                      icon={
                        calculateDaysToDeadline(reviewDeadline) < 1
                          ? faTriangleExclamation
                          : faInfoCircle
                      }
                    />
                  </Flex>
                </Flex>
              )}
            </Flex>
            <Text textStyle="textBody" padding={4}>
              {description}
            </Text>
            {isAssessmentRequestAccepted() && adjudicationCurrentState !== 'interrupted' && <Flex justifyContent="flex-end" gap={4} padding={4}>
              {adjudicationCurrentState !== 'reviewed' ? (
                <Button
                  variant="primary"
                  onClick={() => setIsSubmitFeedbackModal(prev => !prev)}
                >
                  Submit Feedback
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => setIsReviewFeedbackModal(prev => !prev)}
                >
                  Review Feedback
                </Button>
              )}
              {
                adjudicationCurrentState !== 'reviewed' && (
                  <ContextMenu actions={contextMenuActions} />
                )}
            </Flex>}
          </Flex>
        </Flex>
      }

    </>
  );
};
